import { Plus } from '@phosphor-icons/react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { LinkedRecordBar, PageHeader } from '@/components/core';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useLinkedRecord } from '@/hooks/use-linked-record';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { useDepartmentScope } from '@/modules/shared/hr-lookups';
import { useAuthStore } from '@/state/store/auth';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createToilRequestsTableColumns,
  ToilRequestAction,
} from '../../components/toil-requests-table/toil-requests-table-columns';
import { ToilForEmployeeDialog } from '../../components/toil-for-employee-dialog/toil-for-employee-dialog';
import { DEFAULT_MANAGE_TOIL_REQUESTS_ORDER } from '../../graphql/queries';
import {
  useArchiveToilRequest,
  useDeleteToilRequest,
  useGetToilRequests,
  useReviewToilRequest,
} from '../../hooks/use-toil-manager';
import { ToilRequest, ToilRequestsPagingResult } from '../../types/toil-manager.types';

/** The confirmation of each menu action: title, question and confirm button. */
const CONFIRMATION: Record<ToilRequestAction, { title: string; description: string }> = {
  Approved: { title: 'APPROVE_TOIL_REQUEST', description: 'APPROVE_TOIL_CONFIRMATION' },
  Rejected: { title: 'REJECT_TOIL_REQUEST', description: 'REJECT_TOIL_CONFIRMATION' },
  Archive: { title: 'ARCHIVE_TOIL_REQUEST', description: 'TOIL_REQUEST_ARCHIVE_WARNING' },
  Delete: { title: 'DELETE_TOIL_REQUEST', description: 'TOIL_REQUEST_DELETE_WARNING' },
};

const CONFIRM_TEXT: Record<ToilRequestAction, string> = {
  Approved: 'APPROVE',
  Rejected: 'REJECT',
  Archive: 'ARCHIVE',
  Delete: 'DELETE',
};

const RequestsTab = ({ isArchived }: { isArchived: boolean }) => {
  const { t } = useTranslation();
  // The TOIL workflows check these too; they only decide which actions the row menu offers.
  const canReview = useHasPermission(HRM_PERMISSIONS.TOIL.REVIEW);
  const canArchive = useHasPermission(HRM_PERMISSIONS.TOIL.ARCHIVE);
  const canDelete = useHasPermission(HRM_PERMISSIONS.TOIL.DELETE);
  const currentUserId = useAuthStore((state) => state.user?.itemId ?? '');

  // A notification's link lists only its request.
  const { linkedId, clearLinked } = useLinkedRecord();
  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_MANAGE_TOIL_REQUESTS_ORDER,
    linkedId,
  });
  // The request and the action picked from its menu, awaiting confirmation.
  const [target, setTarget] = useState<{
    request: ToilRequest;
    action: ToilRequestAction;
  } | null>(null);

  const { departments, getDepartmentIdCondition } = useDepartmentScope();

  // The department scope always applies, so a reviewer limited to some departments never sees
  // requests outside them; the Department column's filter only narrows within it (mergeWhere).
  const departmentCondition = getDepartmentIdCondition('');
  const filter = {
    IsArchived: isArchived,
    ...(departmentCondition ? { DepartmentId: departmentCondition } : {}),
  };
  const { data, isLoading, error } = useGetToilRequests({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere(filter, columnWhere),
    order,
  });
  const result = (data as { ToilRequests?: ToilRequestsPagingResult })?.ToilRequests;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { mutateAsync: reviewToilRequest, isPending: isReviewing } = useReviewToilRequest();
  const { mutateAsync: archiveToilRequest, isPending: isArchiving } = useArchiveToilRequest();
  const { mutateAsync: deleteToilRequest, isPending: isDeleting } = useDeleteToilRequest();
  const isRunning = isReviewing || isArchiving || isDeleting;

  // A refusal is toasted by the mutation's global error handler; the modal closes either way.
  const handleConfirm = async () => {
    if (!target) return;
    const { request, action } = target;
    try {
      if (action === 'Delete') await deleteToilRequest(request.ItemId);
      else if (action === 'Archive') await archiveToilRequest(request.ItemId);
      else await reviewToilRequest({ itemId: request.ItemId, status: action });
    } catch {
      // Already toasted.
    } finally {
      setTarget(null);
    }
  };

  // No details dialog: every action is picked from the row's menu and only confirmed.
  const columns = createToilRequestsTableColumns({
    t,
    onAction: (request, action) => setTarget({ request, action }),
    canReview,
    canArchive,
    canDelete,
    currentUserId,
    departments,
  });
  const confirmation = target ? CONFIRMATION[target.action] : undefined;
  const isDestructive = target?.action === 'Rejected' || target?.action === 'Delete';

  return (
    <div className="flex flex-col gap-4">
      <LinkedRecordBar linkedId={linkedId} onShowAll={clearLinked} />
      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <ConfirmationModal
        open={Boolean(target)}
        onOpenChange={(open) => !open && !isRunning && setTarget(null)}
        title={confirmation ? t(confirmation.title) : ''}
        description={confirmation ? t(confirmation.description) : ''}
        confirmText={target ? CONFIRM_TEXT[target.action] : 'CONFIRM'}
        confirmClassName={isDestructive ? 'destructive' : undefined}
        preventAutoClose
        confirmDisabled={isRunning}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

export const ToilManagerPage = () => {
  const { t } = useTranslation();
  const canRequestForOthers = useHasPermission(HRM_PERMISSIONS.TOIL.REQUEST_FOR_OTHERS);
  const [isAddOpen, setIsAddOpen] = useState(false);

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title={t('TOIL_MANAGER')}
        actions={
          canRequestForOthers
            ? [
                {
                  label: t('ADD_TOIL_FOR_EMPLOYEE'),
                  icon: <Plus className="size-5" />,
                  onClick: () => setIsAddOpen(true),
                },
              ]
            : []
        }
      />

      {/* The filters are the columns' own (the Department column's included); each tab keeps its own. */}
      <Tabs defaultValue="all">
        <TabsList className="max-sm:w-full max-sm:justify-start max-sm:overflow-x-auto">
          <TabsTrigger value="all" className="max-sm:flex-1">
            {t('ALL_REQUESTS')}
          </TabsTrigger>
          <TabsTrigger value="archived" className="max-sm:flex-1">
            {t('ARCHIVED')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="all">
          <RequestsTab isArchived={false} />
        </TabsContent>
        <TabsContent value="archived">
          <RequestsTab isArchived />
        </TabsContent>
      </Tabs>

      {canRequestForOthers && (
        <ToilForEmployeeDialog open={isAddOpen} onOpenChange={setIsAddOpen} />
      )}
    </div>
  );
};
